import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Tabs } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import SprZalCard, { ZAL_TAB_ITEMS, ZAL_TAB_KEYS } from "./spr-zal-card";
import "./spr-zal-drawer.css";

const DRAWER_WIDTH_KEY = "planOnline.sprZal.cardDrawer.width";
const MIN_WIDTH = 560;
const MAX_WIDTH = 1400;
const DEFAULT_WIDTH = 960;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const SprZalDrawer = ({
  open,
  idZal,
  isNew = false,
  idUpper,
  parentOptions,
  onClose,
  onSaved
}) => {
  const [size, setSize] = useState(loadWidth);
  const [activeTab, setActiveTab] = useState(ZAL_TAB_KEYS.MAIN);

  useEffect(() => {
    if (open) setActiveTab(ZAL_TAB_KEYS.MAIN);
  }, [open, idZal, isNew]);

  useEffect(() => {
    if (isNew && activeTab !== ZAL_TAB_KEYS.MAIN) {
      setActiveTab(ZAL_TAB_KEYS.MAIN);
    }
  }, [isNew, activeTab]);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const drawerMaxSize = maxAllowedWidth();

  const tabItems = useMemo(() => {
    if (isNew) {
      return ZAL_TAB_ITEMS.filter((item) => item.key === ZAL_TAB_KEYS.MAIN);
    }
    return ZAL_TAB_ITEMS;
  }, [isNew]);

  const onTabChange = useCallback(
    (key) => {
      if (isNew && key !== ZAL_TAB_KEYS.MAIN) return;
      setActiveTab(key);
    },
    [isNew]
  );

  const title = (
    <div className="spr-zal-drawer__title">
      <Tabs
        className="spr-zal-drawer__tabs"
        size="small"
        activeKey={activeTab}
        onChange={onTabChange}
        items={tabItems}
      />
    </div>
  );

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={drawerMaxSize}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      rootClassName="spr-zal-drawer"
      extra={
        <div className="spr-zal-drawer__header-extra">
          <ReportButton
            vid={REPORT_VID.CABINETS}
            id={idZal}
            compact
            disabled={isNew || idZal == null}
          />
          <Button
            type="text"
            className="spr-zal-drawer__close"
            icon={<CloseOutlined />}
            aria-label="Закрыть"
            title="Закрыть"
            onClick={onClose}
          />
        </div>
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idZal != null || isNew) ? (
        <SprZalCard
          idZal={idZal}
          isNew={isNew}
          idUpper={idUpper}
          open={open}
          activeTab={activeTab}
          parentOptions={parentOptions}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprZalDrawer;
